import * as angular from "angular";
import { Campaign } from "../models/campaign.model";
import { Organisation } from "../models/organisation.model";
import { OrganisationService } from "../services/organisation.service";
import { PermissionCheck } from "../shared/permission";
import { CredentialsManager } from "../shared/credentials-manager";
import { CampaignService } from "../services/campaign.service";
import { SubscriptionService } from "../services/subscription.service";
import { getPlainObjectOf } from "../utils/class.utils";

export class CampaignDataProvider {
    public activeCampaign: Campaign;
    public activeOrganisation: Organisation;
    public organisations: Organisation[] = null;
    public campaignSwitching?: boolean;

    private upgrade = null;
    private customfieldsColumns = null;
    private sortedCampaignKinds: any = [];
    private campaignKinds: any = [];
    private organisationsPromise: Promise<any>;
    private userCustomFieldsColumnsPromise: Promise<any>;
    private campaignKindPromise: Promise<any>;
    public isLeaderView: boolean;
    public showingDummyData: boolean;
    public canEnableSampleData: boolean;

    private __env: any;
    private $cookies: any;
    private $q: any;
    private $location: any;
    private $window: any;
    private $route: any;

    private permissionCheck: PermissionCheck;
    private campaignService: CampaignService;
    private organisationService: OrganisationService;
    private credentialsManager: CredentialsManager;
    private subscriptionService: SubscriptionService;
    private modelsUtils: any;

    constructor(
        $q: any,
        $cookies: any,
        __env: any,
        $location: any,
        $window: any,
        $route: any,
        ModelsUtils: any,
        OrganisationService: OrganisationService,
        CampaignService: CampaignService,
        CredentialsManager: CredentialsManager,
        PermissionCheck: PermissionCheck,
        SubscriptionService: SubscriptionService,
        __app_type: string
    ) {
        this.__env = __env;
        this.$q = $q;
        this.$location = $location;
        this.$window = $window;
        this.$route = $route;
        this.$cookies = $cookies;
        this.isLeaderView = __app_type == "LEADER";

        this.permissionCheck = PermissionCheck;
        this.campaignService = CampaignService;
        this.organisationService = OrganisationService;
        this.credentialsManager = CredentialsManager;
        this.subscriptionService = SubscriptionService;
        this.modelsUtils = ModelsUtils;

        this.activeCampaign = this.modelsUtils.campaign({});
    }

    setActiveCampaign(campaign: Campaign): Campaign {
        this.upgrade = null;
        let tmp = getPlainObjectOf(campaign);

        for (const property in tmp) {
            if (tmp.hasOwnProperty(property)) {
                let value = decodeURIComponent(tmp[property]);
                tmp[property] = value == "null" ? null : value;
            }
        }
        tmp["trial_days"] = this.calculateTrialDaysRemaining(tmp);
        tmp["trial_days_rounded"] = Math.round(tmp["trial_days"]);

        this.activeCampaign.setData(tmp);
        this.setCampaignCookie(this.activeCampaign);

        this.permissionCheck.setHasStripeSubscription(
            this.activeCampaign.subscriptionid != null &&
                this.activeCampaign.subscriptionid != ""
        );
        this.permissionCheck.setIsYearlySubscrition(
            this.activeCampaign.cpinterval != null &&
                this.activeCampaign.cpinterval === "Yearly"
        );

        return this.activeCampaign;
    }

    getActiveCampaign(): Campaign {
        return this.activeCampaign;
    }

    updateCampaign(campaign: Campaign | any): Promise<any> {
        const deferred = this.$q.defer();
        this.campaignService.update(campaign).then((response) => {
            const json = response["data"];
            if (json.success) {
                this.clearOrganisations();
                angular.extend(this.activeCampaign, json.data.attributes);
                this.setActiveCampaign(this.activeCampaign);
                deferred.resolve(json);
            }
            deferred.reject(json);
        });
        return deferred.promise;
    }

    hasSampleData(): Promise<any> {
        const deferred = this.$q.defer();
        if (this.isLeaderView) {
            deferred.resolve(false);
            return deferred.promise;
        }
        this.campaignService.hasSampleData().then((response) => {
            const json = response["data"];
            if (json.success) {
                this.showingDummyData = json.data;
                deferred.resolve(json.data);
            }
            deferred.reject(json);
        });
        return deferred.promise;
    }

    ipInfo(): Promise<any> {
        const deferred = this.$q.defer();

        this.campaignService.ipInfo().then((response: any): void => {
            const json = response["data"];
            if (json.success) {
                deferred.resolve(json.data);
            }
            deferred.reject(json);
        });
        return deferred.promise;
    }

    allowedToEnableSampleData(): Promise<any> {
        const deferred = this.$q.defer();
        if (this.isLeaderView) {
            deferred.resolve(false);
            return deferred.promise;
        }
        this.campaignService.canEnableSampleData().then((response) => {
            const json = response["data"];
            if (json.success) {
                this.canEnableSampleData = json.data;
                deferred.resolve(json.data);
            }
            deferred.reject(json);
        });
        return deferred.promise;
    }

    deleteSampleData(): Promise<any> {
        const deferred = this.$q.defer();
        this.campaignService.deleteSampleData().then((response) => {
            const json = response["data"];
            if (json.success) {
                deferred.resolve(json);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    restoreSampleData(): Promise<any> {
        const deferred = this.$q.defer();
        this.campaignService.restoreSampleData().then((response) => {
            const json = response["data"];
            if (json.success) {
                deferred.resolve(json);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    createTailoredData(campaignKind: any): Promise<any> {
        const deferred = this.$q.defer();
        this.campaignService
            .createTailoredData(campaignKind)
            .then((response) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json);
                }
                deferred.reject(json);
            });
        return deferred.promise;
    }

    clearOrganisations(): void {
        this.organisations = null;
        this.activeOrganisation = null;
        this.organisationsPromise = null;
    }

    clearAll(): void {
        this.customfieldsColumns = null;
        this.userCustomFieldsColumnsPromise = null;
    }

    setCampaignCookie(campaign: Campaign): void {
        let cookieCampaign = getPlainObjectOf(campaign);
        if (cookieCampaign.hasOwnProperty("localInfo")) {
            delete cookieCampaign["localInfo"];
        }
        for (let property in cookieCampaign) {
            if (cookieCampaign.hasOwnProperty(property)) {
                cookieCampaign[property] = encodeURIComponent(
                    cookieCampaign[property]
                );
            }
        }
        this.$cookies.remove("campaign");

        let domain = this.__env.host;

        if (!domain.includes("localhost")) {
            domain = "." + domain;
        }

        this.$cookies.putObject("campaign", cookieCampaign, {
            domain: domain,
            path: "/",
        });
    }

    updateIntercomUser(): void {
        this.$window.Intercom("boot", this.generateIntercomUserObject());
    }

    initializeIntercomUser(): void {
        window.intercomSettings = this.generateIntercomUserObject();
    }

    generateIntercomUserObject(): any {
        const uid = this.__env.test
            ? "Test" + this.$cookies.get("uid")
            : this.$cookies.get("uid");

        return {
            app_id: this.__env.intercom_id,
            user_id: uid,
            user_hash: this.$cookies.get("user_hash"),
            name:
                this.$cookies.get("ufname") + " " + this.$cookies.get("ulname"),
            email: this.$cookies.get("uemail"),
            created_at: this.$cookies.get("ujoindate"),
            product_name: this.__env.product_name,
            companies: [this.companyFromCampaign(this.activeCampaign)],
        };
    }

    companyFromCampaign(campaign: Campaign): any {
        let data: any = {
            company_id: campaign.campaignid,
            name: campaign.campaignname,
            region: campaign.campaignregion,
            status: campaign.campaignstatusname,
            plan_country: campaign.cpcountry,
            interval: campaign.cpinterval,
            plan: campaign.cpname,
            monthly_spend: campaign.cpprice,
            nb_nation: campaign.nbnid,
            nb_slug: campaign.slug,
            remote_created_at: campaign.campaigntimestamp,
        };

        if (campaign.campaign_kind_category) {
            data.campaign_kind = campaign.campaign_kind_category;
        }
        if (campaign.campaign_kind_subcategory) {
            data.campaign_kind_subcategory = campaign.campaign_kind_subcategory;
        }

        return data;
    }

    calculateTrialDaysRemaining(campaign): number {
        const oneDay = 24 * 60 * 60 * 1000;
        const camapignStart = new Date();
        let days = null;
        if (!!campaign.campaignexpire) {
            const trialEnd = new Date(
                campaign.campaignexpire.split(" ").join("T") + "Z"
            );
            days = (trialEnd.getTime() - camapignStart.getTime()) / oneDay;
        }

        //if they have already added their card, and their trial is over
        if (campaign.subscriptionstatus == "active" && days === 0) {
            days = null;
        }

        return days;
    }

    getOrganisations(force: boolean = false): Promise<Organisation> {
        if (!force && this.organisations !== null) {
            return this.$q.resolve(
                this.modelsUtils.organisation(this.activeOrganisation)
            );
        } else if (this.organisationsPromise) {
            //Has been called already and has not resolved yet.
            return this.organisationsPromise;
        } else {
            const deferred = this.$q.defer();

            this.organisationService.all().then((response) => {
                const json = response["data"];
                if (json.success) {
                    this.setOrganisations(json.data);
                    deferred.resolve(
                        this.modelsUtils.organisation(this.activeOrganisation)
                    );
                } else {
                    this.$window.location =
                        "/logout.php?reason=NoCampaigns&r=" +
                        this.$location.url();
                }
            });

            this.organisationsPromise = deferred.promise;
            return this.organisationsPromise;
        }
    }

    allCampaigns(onlyCurrentOrg?: any): Promise<Campaign[]> {
        const deferred = this.$q.defer();
        let campaigns: Campaign[] = [];
        this.getOrganisations().then(() => {
            this.organisations.forEach((organisation: Organisation) => {
                if (
                    (onlyCurrentOrg == null || onlyCurrentOrg === true) &&
                    organisation !== null &&
                    this.activeOrganisation !== null
                ) {
                    if (organisation.oid == this.activeOrganisation.oid) {
                        organisation.campaigns.forEach((campaign: any) => {
                            campaigns.push(this.modelsUtils.campaign(campaign));
                        });
                    }
                }
            });
            deferred.resolve(campaigns);
        });
        return deferred.promise;
    }

    ownerCampaigns(uid: any): Promise<any> {
        const deferred = this.$q.defer();
        let ownerCampaigns: Campaign[] = [];
        this.getOrganisations().then(() => {
            this.organisations.forEach((organisation) => {
                organisation.campaigns.forEach((campaign) => {
                    if (campaign.campaignowner == uid)
                        ownerCampaigns.push(
                            this.modelsUtils.campaign(campaign)
                        );
                });
            });
            deferred.resolve(ownerCampaigns);
        });
        return deferred.promise;
    }

    cancelCampaigns(selectedCampaigns, feedback, reason): Promise<any> {
        const deferred = this.$q.defer();
        this.campaignService
            .cancelCampaigns({
                campaigns: selectedCampaigns,
                feedback: feedback,
                reason: reason,
            })
            .then(
                (response) => {
                    const json = response["data"];
                    angular.forEach(selectedCampaigns, (campaign) => {
                        this.spliceCampaign(campaign.campaignid);
                    });
                    if (json.data) {
                        if (json.activeCampaign) {
                            this.credentialsManager
                                .reauth(
                                    json.activeCampaign.campaignid,
                                    json.activeCampaign.oid || null
                                )
                                .then(() => {
                                    this.setActiveCampaign(json.activeCampaign);
                                    this.setOrganisations(this.organisations);
                                    deferred.resolve(json);
                                });
                        } else {
                            deferred.resolve(json);
                        }
                        return;
                    }
                    deferred.reject();
                },
                () => deferred.reject()
            );
        return deferred.promise;
    }

    info(id: string): Promise<any> {
        const deferred = this.$q.defer();

        this.campaignService.info({ id: id }).then(
            (response) => {
                const json = response["data"];
                if (json.data) {
                    let campaign = this.modelsUtils.campaign(json.data);
                    deferred.resolve(campaign);
                }
                deferred.reject();
            },
            () => deferred.reject()
        );
        return deferred.promise;
    }

    setupSteps(): Promise<any> {
        const deferred = this.$q.defer();

        this.campaignService.setupSteps().then(
            (response) => {
                const json = response["data"];
                if (json.data) {
                    deferred.resolve(json.data);
                }
                deferred.reject();
            },
            () => deferred.reject()
        );
        return deferred.promise;
    }

    markSetupStepCompleted(stepid: number): Promise<any> {
        const deferred = this.$q.defer();

        this.campaignService.markSetupStepCompleted(stepid).then(
            (response) => {
                const json = response["data"];
                if (json.data) {
                    deferred.resolve(json.data);
                }
                deferred.reject();
            },
            () => deferred.reject()
        );
        return deferred.promise;
    }

    checkCampaignUpgradePending(): Promise<any> {
        const deferred = this.$q.defer();
        if (this.upgrade !== null) {
            return this.$q.resolve(this.upgrade);
        } else {
            this.campaignService
                .checkCampaignUpgradePending({
                    id: this.activeCampaign.campaignid,
                })
                .then(
                    (response) => {
                        const json = response["data"];
                        if (json.data) {
                            this.upgrade = json.data;
                            deferred.resolve(this.upgrade);
                        }
                        deferred.reject();
                    },
                    () => deferred.reject()
                );
            return deferred.promise;
        }
    }

    acknowledgeUpgrade(data): Promise<any> {
        const deferred = this.$q.defer();

        this.campaignService.acknowledgeUpgrade(data).then(
            (response) => {
                const json = response["data"];
                if (json.data) {
                    this.upgrade.acknowleged = json.data;
                    deferred.resolve(json.data);
                }
                deferred.reject();
            },
            () => deferred.reject()
        );
        return deferred.promise;
    }

    setOrganisations(data: any): void {
        if (data) {
            for (let i = 0; i < data.length; i++) {
                for (let j = 0; j < data[i].campaigns.length; j++) {
                    data[i].campaigns[j] = this.modelsUtils.campaign(
                        data[i].campaigns[j]
                    );
                    if (
                        data[i].campaigns[j].campaignid ==
                        this.activeCampaign.campaignid
                    ) {
                        this.activeCampaign.setData(data[i].campaigns[j]);
                        this.activeOrganisation = this.modelsUtils.organisation(
                            data[i]
                        );
                    }
                }
            }
            this.organisations = data;
        }
    }

    changeLiveCampaign(campaign, redirect?): Promise<any> {
        return this.$q((resolve, reject) => {
            if (campaign.campaignid == this.activeCampaign.campaignid) {
                //Already active just currently on leader view.
                resolve(this.activeCampaign);
                return;
            }
            this.campaignService.live(campaign).then((response) => {
                const json = response["data"];
                if (json.success) {
                    this.$cookies.remove("signedIn");
                    const newOID = json.campaign.oid || null;
                    json.campaign.redirect = json.redirect;
                    this.credentialsManager
                        .reauth(json.campaign.campaignid, newOID)
                        .then(() => {
                            this.setActiveCampaign(json.campaign);
                            this.setOrganisations(this.organisations);
                            resolve(this.activeCampaign);
                            if (
                                ["/expired"].indexOf(this.$location.path()) > -1
                            ) {
                                this.$window.location = "/#/";
                            }
                        }, (error: any) => reject(error));
                } else if (angular.isDefined(json.region)) {
                    if (json.handoff) {
                        // Admins carry their session across regions instead
                        // of re-signing in.
                        this.credentialsManager.handoff(
                            json.region,
                            json.handoff,
                            "/#/"
                        );
                        return;
                    }
                    this.$window.location =
                        this.credentialsManager.crossRegionHost(json.region) +
                        "/signin.php#?message=Your+campaign+is+based+in+a+different+region+please+Sign+In+again.";
                } else {
                    reject(json);
                }
            }, (error: any) => reject(error));
        });
    }

    campaignWithID(campaignid): Campaign | null {
        for (let i = 0; i < this.organisations.length; i++) {
            for (let j = 0; j < this.organisations[i].campaigns.length; j++) {
                if (
                    this.organisations[i].campaigns[j].campaignid == campaignid
                ) {
                    return this.organisations[i].campaigns[j];
                }
            }
        }
        return null;
    }

    spliceCampaign(campaignid): Campaign | null {
        for (let i = 0; i < this.organisations.length; i++) {
            for (let j = 0; j < this.organisations[i].campaigns.length; j++) {
                if (
                    this.organisations[i].campaigns[j].campaignid == campaignid
                ) {
                    return this.organisations[i].campaigns.splice(j, 1);
                }
            }
        }
        return null;
    }

    isOrganisationCampaign(campaignid): Promise<any> {
        const deferred = this.$q.defer();
        this.getOrganisations().then(() => {
            for (let i = 0; i < this.organisations.length; i++) {
                if (this.organisations[i].oid === null) {
                    for (
                        let j = 0;
                        j < this.organisations[i].campaigns.length;
                        j++
                    ) {
                        if (
                            this.organisations[i].campaigns[j].campaignid ===
                            campaignid
                        ) {
                            deferred.resolve(false);
                        }
                    }
                }
            }
            deferred.resolve(true);
        });
        return deferred.promise;
    }

    setEssentialsModeForCampaigns(
        campaigns,
        restart_date,
        interval?
    ): Promise<any> {
        const deferred = this.$q.defer();
        interval = interval || "Monthly";
        if (!campaigns) {
            campaigns = [this.activeCampaign];
        }

        this.subscriptionService
            .essentials({
                campaigns: campaigns,
                restart_date: restart_date,
                interval: interval,
            })
            .then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success && json.data) {
                        this.clearOrganisations();
                        const updatedCampaigns = json.data;
                        for (let i = 0; i < updatedCampaigns.length; i++) {
                            if (
                                updatedCampaigns[i].campaignid ==
                                this.activeCampaign.campaignid
                            ) {
                                updatedCampaigns[i].cpname = null;
                                this.setActiveCampaign(updatedCampaigns[i]);
                            } else {
                                let campaign = this.campaignWithID(
                                    updatedCampaigns[i].campaignid
                                );
                                if (campaign) {
                                    campaign.campaignstatusid = 5;
                                }
                            }
                        }
                        deferred.resolve(json);
                    }
                    deferred.reject();
                },
                () => deferred.reject()
            );
        return deferred.promise;
    }

    getCustomFieldColumns(): Promise<any> {
        const deferred = this.$q.defer();
        if (this.customfieldsColumns !== null) {
            deferred.resolve(this.customfieldsColumns);
            this.userCustomFieldsColumnsPromise = deferred.promise;
            return this.userCustomFieldsColumnsPromise;
        } else if (this.userCustomFieldsColumnsPromise) {
            return this.userCustomFieldsColumnsPromise;
        } else {
            this.campaignService.userCustomFields().then(
                (response) => {
                    const json = response["data"].data;
                    this.customfieldsColumns = [];
                    for (let i = 0; i < json.length; i++) {
                        this.customfieldsColumns.push(json[i]);
                    }
                    deferred.resolve(this.customfieldsColumns);
                },
                function (error) {
                    this.customfieldsColumns = [];
                    deferred.reject(error);
                }
            );
        }
        this.userCustomFieldsColumnsPromise = deferred.promise;
        return this.userCustomFieldsColumnsPromise;
    }

    processSocketCreate(socketData) {
        if (
            this.activeCampaign.campaignid &&
            this.activeCampaign.canvassingType
        ) {
            const index = this.indexCanvassType(socketData);
            if (index == -1) {
                this.activeCampaign.canvassingType.push(socketData);
                this.$route.reload();
            }
        }
    }

    processSocketDelete(socketData) {
        if (
            this.activeCampaign.campaignid &&
            this.activeCampaign.canvassingType
        ) {
            const index = this.indexCanvassType(socketData);
            if (index > -1) {
                this.activeCampaign.canvassingType.splice(index, 1);
                this.$route.reload();
            }
        }
    }

    indexCanvassType(socketData): number {
        for (let i = 0; i < this.activeCampaign.canvassingType.length; i++) {
            if (
                this.activeCampaign.campaignid &&
                this.activeCampaign.canvassingType[i].ctid === socketData.ctid
            ) {
                return i;
            }
        }
        return -1;
    }

    getKinds(): Promise<any> {
        const deferred = this.$q.defer();
        if (this.campaignKinds.length > 0) {
            deferred.resolve(this.campaignKinds);
            this.campaignKindPromise = deferred.promise;
            return this.campaignKindPromise;
        } else if (this.campaignKindPromise) {
            return this.campaignKindPromise;
        } else {
            this.campaignService.getKinds().then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.campaignKinds = this.sortCampaignKinds(json.data);
                        deferred.resolve(this.campaignKinds);
                    }
                },
                function (error: any) {
                    this.campaignKinds = [];
                    deferred.reject(error);
                }
            );
        }
        this.campaignKindPromise = deferred.promise;
        return this.campaignKindPromise;
    }

    sortCampaignKinds(kinds: any[]): any[] {
        const sorted: any = [];
        kinds.forEach((item: any) => {
            const existingCategory: any = sorted.find(
                (category: any) => category.name === item.category
            );

            if (existingCategory) {
                existingCategory.subcategories.push({
                    id: item.id,
                    name: item.subcategory,
                });
            } else {
                sorted.push({
                    name: item.category,
                    subcategories: [{ id: item.id, name: item.subcategory }],
                });
            }
        });
        return sorted;
    }
}

angular
    .module("vcDataProvider")
    .service("CampaignDataProvider", CampaignDataProvider);
